import { filterBloodDonationTypeList } from '@src/data/filter';
import filterAtom from '@src/recoils/atom/filter';
import keywordAtom from '@src/recoils/atom/keyword';
import { devices } from '@src/styles/theme';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import useDragScroll from '@src/hooks/useDragScroll';
import { useRecoilState } from 'recoil';
import styled, { keyframes } from 'styled-components';

/**
 * 헌혈요청 목록 상단 (Pple Blood Request List 디자인 이식).
 *
 * 구성: 타이틀 + 총 건수 + 요청글 작성
 *      → 필터 카드(검색 + 긴급만 보기 / 혈액형·헌혈 종류·지역 칩)
 *      → 총 N건 + 정렬 칩
 *
 * 백엔드 지원 범위에 맞춘 부분:
 * - 혈액형은 검색 파라미터가 abo(A/B/O/AB)까지만이라 Rh(+/-)는 칩으로 나누지 않는다.
 * - 정렬 3번째는 디자인의 '참여 많은순' 대신 '남은 혈액팩순'(사용자 지정, SortBy.REMAINING).
 * - 지역 칩은 디자인의 6개에 대전·광주를 더한 상위 집합이다.
 */
type Props = {
  totalCount?: number;
  /** 긴급만 보기 토글 (마감 3일 이내) */
  urgentOnly?: boolean;
  onToggleUrgent?: () => void;
};

const ABO_CHIPS = [
  { value: '', label: '전체' },
  { value: 'O', label: 'O형' },
  { value: 'A', label: 'A형' },
  { value: 'B', label: 'B형' },
  { value: 'AB', label: 'AB형' },
];

/**
 * 지역 칩 — 백엔드는 region 접두어(예: "서울 서초구")로 매칭한다.
 * 글쓰기(DonationDetailFields)의 지역 선택지와 같은 17개 시·도를 쓴다.
 * 둘이 다르면 등록은 되는데 필터에서 안 걸리는 지역이 생긴다.
 */
const REGION_CHIPS = [
  '전체',
  '서울',
  '경기',
  '인천',
  '부산',
  '대구',
  '대전',
  '광주',
  '울산',
  '세종',
  '강원',
  '충북',
  '충남',
  '전북',
  '전남',
  '경북',
  '경남',
  '제주',
];

/** 정렬 — 백엔드 SortBy.DONATION 값과 1:1 */
const SORT_CHIPS = [
  { value: 'LAST_RENEWED_AT', label: '최신순' },
  { value: 'DEADLINE', label: '마감 임박순' },
  { value: 'REMAINING', label: '남은 혈액팩순' },
];

/** 마우스로 끌어서도 넘길 수 있는 칩 줄 (터치는 네이티브 스크롤) */
const ChipRow = ({ children }: { children: React.ReactNode }) => {
  const { ref, handlers } = useDragScroll();
  return (
    <Chips ref={ref} {...handlers}>
      {children}
    </Chips>
  );
};

const DonationListHeader = ({
  totalCount,
  urgentOnly,
  onToggleUrgent,
}: Props) => {
  const router = useRouter();
  const [filter, setFilter] = useRecoilState(filterAtom);
  const [keyword, setKeyword] = useRecoilState(keywordAtom);
  const [input, setInput] = useState(keyword ?? '');

  const labelOf = (type: string) =>
    filter.find((f) => f.type === type)?.label ?? '';

  const setFilterLabel = (type: string, label: string) => {
    setFilter([...filter.filter((f) => f.type !== type), { type, label }]);
  };

  // 초기 라벨('혈액형','헌혈 종류')은 미선택 상태로 본다 → '전체' 칩이 활성.
  const isAllSelected = (type: string, defaultLabel: string) => {
    const l = labelOf(type);
    return l === defaultLabel || l === '전체' || !l;
  };

  return (
    <Wrap>
      <TitleRow>
        <Title>수혈 요청</Title>
        {totalCount != null && (
          <SubText>
            지금 도움이 필요한 이웃 <b>{totalCount}건</b>
          </SubText>
        )}
        <WriteButton
          onClick={() => router.push('/community/edit?type=donation')}
        >
          요청글 작성
        </WriteButton>
      </TitleRow>

      <FilterCard>
        <SearchRow>
          <SearchBox>
            <SearchIcon />
            <SearchInput
              value={input}
              placeholder="병원명 · 지역으로 검색"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setKeyword(input);
              }}
            />
          </SearchBox>
          {onToggleUrgent && (
            <UrgentToggle active={!!urgentOnly} onClick={onToggleUrgent}>
              <PulseDot />
              긴급만 보기
            </UrgentToggle>
          )}
        </SearchRow>

        <FacetGroup>
          <FacetLabel>혈액형</FacetLabel>
          <ChipRow>
            {ABO_CHIPS.map((chip) => {
              const active = chip.value
                ? labelOf('abo') === chip.label
                : isAllSelected('abo', '혈액형');
              return (
                <Chip
                  key={chip.label}
                  active={active}
                  onClick={() =>
                    setFilterLabel('abo', chip.value ? chip.label : '전체')
                  }
                >
                  {chip.label}
                </Chip>
              );
            })}
          </ChipRow>
        </FacetGroup>

        <FacetGroup>
          <FacetLabel>헌혈 종류</FacetLabel>
          <ChipRow>
            {filterBloodDonationTypeList.map((item) => {
              const short = item.label.split(' ')[0]; // '전혈 (RBC...)' → '전혈'
              const active = item.value
                ? labelOf('bloodProduct') === item.label
                : isAllSelected('bloodProduct', '헌혈 종류');
              return (
                <Chip
                  key={item.label}
                  active={active}
                  onClick={() =>
                    setFilterLabel(
                      'bloodProduct',
                      item.value ? item.label : '전체'
                    )
                  }
                >
                  {short}
                </Chip>
              );
            })}
          </ChipRow>
        </FacetGroup>
        <FacetGroup>
          <FacetLabel>지역</FacetLabel>
          <ChipRow>
            {REGION_CHIPS.map((label) => {
              const active =
                label === '전체'
                  ? isAllSelected('region', '지역')
                  : labelOf('region') === label;
              return (
                <Chip
                  key={label}
                  active={active}
                  onClick={() => setFilterLabel('region', label)}
                >
                  {label}
                </Chip>
              );
            })}
          </ChipRow>
        </FacetGroup>
      </FilterCard>

      <ResultRow>
        {totalCount != null && (
          <ResultCount>
            총 <b>{totalCount}</b>건
          </ResultCount>
        )}
        <Sorts>
          {SORT_CHIPS.map((s) => (
            <SortChip
              key={s.label}
              active={labelOf('sortBy') === s.label}
              onClick={() => setFilterLabel('sortBy', s.label)}
            >
              {s.label}
            </SortChip>
          ))}
        </Sorts>
      </ResultRow>
    </Wrap>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
`;
const Title = styled.h1`
  margin: 0;
  font-family: 'Var', sans-serif;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.6px;
  color: #222222;
  @media ${devices.tablet} {
    font-size: 24px;
  }
`;
const SubText = styled.span`
  font-size: 14px;
  color: #767676;
  padding-bottom: 5px;
  b {
    color: #ff4651;
  }
`;
const WriteButton = styled.button`
  margin-left: auto;
  height: 44px;
  padding: 0 22px;
  border: none;
  border-radius: 22px;
  background: #ff4651;
  color: #ffffff;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: #e03a44;
  }
`;
const FilterCard = styled.div`
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 20px;
  padding: 22px 26px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  @media ${devices.tablet} {
    padding: 14px;
    border-radius: 16px;
    gap: 12px;
  }
`;
const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  @media ${devices.tablet} {
    flex-wrap: wrap;
  }
`;
const SearchBox = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f4f4f4;
  border-radius: 11px;
  padding: 0 14px;
  height: 44px;
`;
/** 디자인의 긴급만 보기 pill — 점이 은은하게 뛴다(pplePulse). */
const pplePulse = keyframes`
  0%   { opacity: 1;   transform: scale(1); }
  50%  { opacity: 0.35; transform: scale(0.82); }
  100% { opacity: 1;   transform: scale(1); }
`;
const UrgentToggle = styled.button<{ active: boolean }>`
  flex: none;
  height: 44px;
  padding: 0 18px;
  border-radius: 22px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid ${({ active }) => (active ? '#FF4651' : '#E6E6E6')};
  background: ${({ active }) => (active ? '#FFF1ED' : '#ffffff')};
  color: ${({ active }) => (active ? '#FF4651' : '#767676')};
  @media ${devices.tablet} {
    height: 38px;
    padding: 0 14px;
    font-size: 13px;
  }
`;
const PulseDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: currentColor;
  animation: ${pplePulse} 1.4s infinite;
`;
const SearchIcon = styled.span`
  flex: none;
  width: 14px;
  height: 14px;
  border: 1.6px solid #9a9a9a;
  border-radius: 50%;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    right: -4px;
    bottom: -4px;
    width: 6px;
    height: 1.6px;
    background: #9a9a9a;
    transform: rotate(45deg);
  }
`;
const SearchInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Regular', sans-serif;
  font-size: 14.5px;
  color: #222222;
  width: 100%;
`;
const FacetGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  /* 모바일: 라벨을 위로 올리고 칩은 한 줄로 가로 스크롤 */
  @media ${devices.tablet} {
    flex-direction: column;
    gap: 6px;
  }
`;
const FacetLabel = styled.span`
  flex: none;
  width: 62px;
  padding-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #9a9a9a;
  @media ${devices.tablet} {
    width: auto;
    padding-top: 0;
    font-size: 12px;
  }
`;
const Chips = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  /* 마우스로 끌어 넘길 때 텍스트가 선택되지 않게 */
  user-select: none;
  /* 모바일: 지역처럼 항목이 많은 줄도 접히지 않고 옆으로 넘긴다 */
  @media ${devices.tablet} {
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0 2px 2px 0;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;
/** 디자인의 chip(): height 32 · padding 0 13 · radius 16 · 12.5px/600 */
const Chip = styled.button<{ active: boolean }>`
  /* 가로 스크롤 줄에서 눌려 글자가 잘리지 않도록 원래 너비를 지킨다. */
  flex: none;
  height: 32px;
  padding: 0 13px;
  border-radius: 16px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid ${({ active }) => (active ? '#FF4651' : '#E6E6E6')};
  background: ${({ active }) => (active ? '#FFF1ED' : '#ffffff')};
  color: ${({ active }) => (active ? '#FF4651' : '#767676')};
`;

/** 정렬 칩은 디자인상 조금 큼: height 34 · padding 0 14 · 13px */
const SortChip = styled(Chip)`
  height: 34px;
  padding: 0 14px;
  border-radius: 17px;
  font-size: 13px;
`;
const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;
const ResultCount = styled.span`
  font-size: 14px;
  color: #767676;
  b {
    color: #222222;
  }
`;
const Sorts = styled.div`
  margin-left: auto;
  display: flex;
  gap: 6px;
`;

export default DonationListHeader;
